import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, MapPin, Calendar, Briefcase, ArrowUpRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import ContactCTA from "../components/ContactCTA";
import { LatestImpactStory } from "../components/LatestImpactStory";
import { projects } from "../data/mock";

export default function ProjectDetail() {
  const { id } = useParams();
  const project = projects.find((p) => p.id === id);
  if (!project) return <Navigate to="/projects" replace />;

  const related = projects.filter((p) => p.id !== project.id).slice(0, 3);

  return (
    <div className="relative">
      <Header />
      <PageHero
        eyebrow={project.type}
        title={project.title}
        subtitle={project.overview}
        image={project.cover}
        crumbs={[{ label: "Projects", to: "/projects" }, { label: project.title }]}
      />

      {/* Meta strip */}
      <section className="py-10 bg-[#0a0a0c] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/45 mb-2 flex items-center gap-2">
              <Briefcase className="w-3.5 h-3.5" /> Client
            </div>
            <div className="font-display text-lg text-white">{project.client}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/45 mb-2 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> Location
            </div>
            <div className="font-display text-lg text-white">{project.location}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/45 mb-2 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" /> Year
            </div>
            <div className="font-display text-lg text-white">{project.year}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/45 mb-2">Category</div>
            <div className="font-display text-lg text-[#F5D000]">{project.category}</div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-20 lg:py-28 bg-[#08080a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-8 space-y-10">
            {[
              { title: "The Challenge", body: project.challenge },
              { title: "The Solution", body: project.solution },
              { title: "The Outcome", body: project.outcome }
            ].map((sec, i) => (
              <div key={sec.title} className="reveal" style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-3">{sec.title}</div>
                <h3 className="font-display text-2xl lg:text-3xl font-bold mb-4 text-white">{sec.title}</h3>
                <p className="text-white/75 leading-relaxed text-base lg:text-lg">{sec.body}</p>
              </div>
            ))}

            {/* Gallery */}
            {project.gallery?.length > 1 && (
              <div className="reveal">
                <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-3">Gallery</div>
                <div className="grid grid-cols-2 gap-3">
                  {project.gallery.map((g, i) => (
                    <img
                      key={i}
                      src={g}
                      alt=""
                      className={`rounded-lg w-full object-cover ${i === 0 ? "col-span-2 aspect-[16/9]" : "aspect-[4/3]"}`}
                      loading="lazy"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Key facts sidebar */}
          <aside className="lg:col-span-4 lg:sticky lg:top-32 self-start">
            <div className="p-7 rounded-2xl bg-[#101014] border border-white/8 reveal">
              <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-5">Key Facts</div>
              <ul className="space-y-3">
                {project.facts.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white/80">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#F5D000] flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/#contact"
                className="btn-amber mt-7 w-full px-5 py-3.5 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2"
              >
                Discuss your project <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <Link
              to="/projects"
              className="mt-6 inline-flex items-center gap-2 text-sm text-white/75 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" /> Back to all projects
            </Link>
          </aside>
        </div>
      </section>

      {/* Related */}
      <section className="py-20 lg:py-28 bg-[#0a0a0c] border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10 reveal">
            <h2 className="font-display text-3xl sm:text-4xl font-bold">
              More <span className="text-amber-accent">projects</span>
            </h2>
            <Link to="/projects" className="text-sm font-medium text-white/80 hover:text-white link-shimmer">
              View all
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                className="proj-card relative block rounded-xl overflow-hidden bg-[#101014] border border-white/5 group"
              >
                <div className="relative aspect-[16/10]">
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent" />
                </div>
                <div className="p-5">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2">{p.category} · {p.year}</div>
                  <div className="font-display text-lg font-bold text-white group-hover:text-[#F5D000] transition-colors">{p.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <LatestImpactStory />
      <ContactCTA />
      <Footer />
    </div>
  );
}
